import { useQuery } from '@tanstack/react-query'
import { Activity, Bell, Bot, ChevronDown, ChevronLeft, ChevronRight, GitBranch, LayoutDashboard, LayoutGrid, Menu, Network, PanelLeftClose, Settings, Workflow, X } from 'lucide-react'
import { useState } from 'react'
import { Link, NavLink, Outlet, useNavigate, useParams } from 'react-router-dom'
import { api, queryKeys } from '../lib/api'
import { Brand } from './Brand'
import { ErrorState, LoadingState } from './States'
import { StatusBadge } from './StatusBadge'

const navItems = [
  { to: 'overview', label: 'Overview', icon: LayoutDashboard },
  { to: 'pipeline', label: 'Knowledge Pipeline', icon: Workflow },
  { to: 'graph', label: 'Knowledge Graph', icon: Network },
  { to: 'chats', label: 'PM Agent', icon: Bot },
  { to: 'activity', label: 'Activity Logs', icon: Activity },
  { to: 'settings', label: 'Settings', icon: Settings },
]

export function AppShell() {
  const { projectId = '' } = useParams()
  const navigate = useNavigate()
  const [mobileOpen, setMobileOpen] = useState(false)
  const [collapsed, setCollapsed] = useState(false)
  const project = useQuery({ queryKey: queryKeys.project(projectId), queryFn: () => api.getProject(projectId), enabled: !!projectId, refetchInterval: (query) => query.state.data?.status === 'provisioning' ? 3000 : false })
  const projects = useQuery({ queryKey: queryKeys.projects, queryFn: api.listProjects })

  if (project.isPending) return <LoadingState />
  if (project.isError) return <main className="standalone-state"><ErrorState message={project.error.message} onRetry={() => project.refetch()} /></main>

  return (
    <div className={`app-shell ${collapsed ? 'app-shell--collapsed' : ''}`}>
      {mobileOpen && <button className="sidebar-backdrop" aria-label="Close navigation" onClick={() => setMobileOpen(false)} />}
      <aside className={`sidebar ${mobileOpen ? 'sidebar--open' : ''}`}>
        <div className="sidebar__header">
          <Link to="/" aria-label="Factspan Knowledge Platform — all projects"><Brand compact={collapsed} /></Link>
          <button className="icon-button sidebar__mobile-close" onClick={() => setMobileOpen(false)} aria-label="Close navigation"><X size={19} /></button>
        </div>
        <Link className="sidebar__back" to="/" onClick={() => setMobileOpen(false)} title={collapsed ? 'All projects' : undefined}>
          {collapsed ? <LayoutGrid size={18} /> : <><ChevronLeft size={16} /><span>All projects</span></>}
        </Link>
        <div className="project-switcher-wrap">
          {!collapsed && <span className="sidebar-label">Current project</span>}
          <div className="project-switcher">
            <span className="project-switcher__avatar">{project.data.name.slice(0, 2).toUpperCase()}</span>
            {!collapsed && <><select value={projectId} onChange={(event) => navigate(`/projects/${event.target.value}/overview`)} aria-label="Switch project">{projects.data?.map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}</select><ChevronDown size={14} /></>}
          </div>
        </div>
        <nav className="sidebar__nav" aria-label="Project navigation">
          {!collapsed && <span className="sidebar-label">Workspace</span>}
          {navItems.map(({ to, label, icon: Icon }) => (
            <NavLink key={to} to={to} onClick={() => setMobileOpen(false)} title={collapsed ? label : undefined}>
              <Icon size={18} /><span>{label}</span>
            </NavLink>
          ))}
        </nav>
        <div className="sidebar__footer">
          {!collapsed && <div className="sidebar__branch"><GitBranch size={15} /><span>{project.data.knowledge_bundle_branch ?? 'Branch pending'}</span></div>}
          <button className="sidebar-collapse" onClick={() => setCollapsed((value) => !value)}><PanelLeftClose size={17} /><span>{collapsed ? 'Expand' : 'Collapse sidebar'}</span></button>
        </div>
      </aside>
      <div className="app-main">
        <header className="topbar">
          <button className="icon-button topbar__menu" onClick={() => setMobileOpen(true)} aria-label="Open navigation"><Menu size={20} /></button>
          <nav className="topbar__project" aria-label="Breadcrumb">
            <Link className="topbar__crumb" to="/">Projects</Link>
            <ChevronRight size={14} aria-hidden="true" />
            <strong>{project.data.name}</strong>
            <StatusBadge status={project.data.status} />
          </nav>
          <div className="topbar__actions">
            <span className="environment-pill"><span /> Production</span>
            <button className="icon-button" aria-label="Notifications"><Bell size={18} /><i /></button>
            <div className="user-avatar" aria-label="Signed in user">VD</div>
          </div>
        </header>
        <main className="content"><Outlet context={{ project: project.data }} /></main>
      </div>
    </div>
  )
}
